import type { Command, UiSnapshot } from '@prism/sim';

/**
 * The support hotbar (playtest request): one square per support tower on the board.
 *
 * Support towers are the only thing the player operates rather than places, and before
 * this the only way to fire one was to find a small identical shape on the board and open
 * its panel — during a wave, while it was going wrong. A fixed row in the HUD means the
 * ability lives somewhere the eye can go straight to, and the square carries every
 * question at once: which tower, which key, and whether it is available.
 *
 * Nothing here decides anything. Which of the four states a square is in comes off the
 * snapshot, because "can I press this" is a rule and a second implementation of it in
 * React would eventually disagree with the sim that actually refuses the command.
 */
export function SurgeBar({
  towers,
  glyphOf,
  colorOf,
  nameOf,
  dispatch,
}: {
  towers: UiSnapshot['placedTowers'];
  glyphOf: (defId: string) => string;
  colorOf: (defId: string) => string;
  nameOf: (defId: string) => string;
  dispatch: (cmd: Command) => void;
}) {
  const support = towers.filter((t) => t.support);
  if (support.length === 0) return null;

  return (
    <div className="surgebar" aria-label="Support abilities">
      {support.map((t, i) => {
        const key = i + 1;
        const state = t.surgeActive
          ? 'on'
          : t.surgeArmed
            ? 'armed'
            : t.surgeReady
              ? 'ready'
              : 'cooling';
        return (
          <button
            key={t.id}
            type="button"
            className={`surgebar__slot surgebar__slot--${state}`}
            // The hotkey only reaches the first nine, so past that the square is the only
            // way to fire it — which is the other reason this row exists.
            disabled={!t.surgeReady}
            title={
              t.surgeActive
                ? `${nameOf(t.defId)} — focused for the rest of this round`
                : t.surgeArmed
                  ? `${nameOf(t.defId)} — armed for the next round`
                  : t.surgeReady
                    ? `${nameOf(t.defId)} — focus it for a whole round${
                        key <= 9 ? ` (key ${key})` : ''
                      }`
                    : `${nameOf(t.defId)} — back in ${t.surgeBackInWaves} round${
                        t.surgeBackInWaves === 1 ? '' : 's'
                      }`
            }
            onClick={() => dispatch({ t: 'surge', tower: t.id })}
          >
            <span className="surgebar__key">{key <= 9 ? key : '·'}</span>
            <span className="surgebar__glyph" style={{ color: colorOf(t.defId) }}>
              {glyphOf(t.defId)}
            </span>
            <span className="surgebar__state">
              {t.surgeActive ? 'ON' : t.surgeArmed ? 'SET' : t.surgeReady ? '◈' : t.surgeBackInWaves}
            </span>
          </button>
        );
      })}
    </div>
  );
}
